/**
 * Sélecteur du mode de récompense (réutilisabilité — Étape 9).
 *
 * Même composant partout : réglage général entreprise ET override par
 * challenge/parcours/duel (avec option « Hériter »). 2 cartes claires :
 *   - Virtuel : points & statut, aucune formalité.
 *   - Cadeaux réels : échangeables en boutique (déclenche la zone URSSAF).
 * Intuitif pour un organisateur / consultant / admin.
 */
import { useTranslation } from "react-i18next";
import { Coins, Gift } from "lucide-react";

const NAVY = "#384872";
const VIOLET = "#5A4BBE";

export type RewardsModeValue = "virtual" | "catalog" | "inherit";

export function RewardsModeSelector({
  value,
  onChange,
  allowInherit = false,
  inheritedMode,
}: {
  value: RewardsModeValue;
  onChange: (v: RewardsModeValue) => void;
  allowInherit?: boolean;
  inheritedMode?: "virtual" | "catalog";
}) {
  const { t } = useTranslation();

  const Card = ({ mode, icon, title, desc }: { mode: "virtual" | "catalog"; icon: React.ReactNode; title: string; desc: string }) => {
    const selected = value === mode;
    return (
      <button
        type="button"
        onClick={() => onChange(mode)}
        className="flex-1 text-left rounded-xl border-2 p-3 md:p-4 transition-shadow hover:shadow-sm"
        style={{ borderColor: selected ? VIOLET : "#E4E7EF", background: selected ? "#5A4BBE0D" : "#fff" }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white shrink-0" style={{ background: selected ? VIOLET : "#9AA0B0" }}>
            {icon}
          </span>
          <span className="font-semibold" style={{ color: NAVY, fontFamily: "Sora, sans-serif" }}>{title}</span>
          {selected && <span className="ml-auto text-xs font-semibold" style={{ color: VIOLET }}>●</span>}
        </div>
        <p className="text-sm text-gray-600">{desc}</p>
      </button>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Card mode="virtual" icon={<Coins className="h-4 w-4" />} title={t("bizRewards.modeVirtual")} desc={t("bizRewards.modeVirtualDesc")} />
        <Card mode="catalog" icon={<Gift className="h-4 w-4" />} title={t("bizRewards.modeCatalog")} desc={t("bizRewards.modeCatalogDesc")} />
      </div>
      {allowInherit && (
        <button
          type="button"
          onClick={() => onChange("inherit")}
          className="mt-2 text-xs font-medium"
          style={{ color: value === "inherit" ? VIOLET : "#9AA0B0" }}
        >
          {value === "inherit" ? "● " : "○ "}
          {t("bizRewards.inherit", { mode: inheritedMode === "catalog" ? t("bizRewards.modeCatalog") : t("bizRewards.modeVirtual") })}
        </button>
      )}
    </div>
  );
}
